import React from "react";
import { Dialog } from "@/components/Dialog";
import { UsersSettingsTab, UsersSettingsTabs } from "./tabs";
import { UserRowModel } from "./SettingsUsersTable";
import { useResendInvitation } from "@/hooks/useResendInvitation";
import { useOnboardFoodicsAccounts } from "@/hooks/useOnboardFoodicsAccounts";
import { useDeactivateUser } from "@/hooks/useDeactivateUser";
import { customToast } from "@/utils/customToast";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/Auth";
import { EditRoleDialog } from "./EditRoleDialog";
import { EditPrivilegesDialog } from "./EditPrivilegesDialog";
import { formatPrivileges } from "./formatPrivileges";

type UserDetailsDialogProps = {
  show: boolean;
  user: UserRowModel;
  selectedTab: UsersSettingsTab;
  businessId?: string;
  onCancel: () => void;
};

type DialogView = "details" | "editRole" | "editPrivileges";

export function UserDetailsDialog({
  show,
  user,
  selectedTab,
  businessId,
  onCancel,
}: UserDetailsDialogProps) {
  const auth = useAuth();
  const isActiveTab = selectedTab === UsersSettingsTabs.active;
  const userIsDemo = (auth.user?.is_demo ?? false) && !isActiveTab;
  const resendInvitationMutation = useResendInvitation();
  const onboardFoodicsAccounts = useOnboardFoodicsAccounts();
  const deactivateUser = useDeactivateUser();
  const [currentView, setCurrentView] = React.useState<DialogView>("details");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

  const userStatus = selectedTab === UsersSettingsTabs.active ? "BI account" : "Foodics account";

  const buttonText =
    selectedTab === UsersSettingsTabs.inactive ? "Invite" : "Send login instructions";

  const handleInvite = async () => {
    try {
      if (selectedTab === UsersSettingsTabs.inactive) {
        if (!businessId) {
          Sentry.captureEvent({
            message: "selected business is not expected to be undefined in invite's handler",
            level: "error",
          });
          return;
        }

        await onboardFoodicsAccounts.mutateAsync({
          businessId,
          accountIds: [user.id],
          userIsDemo,
        });
      } else {
        await resendInvitationMutation.mutateAsync({ userId: user.id, userIsDemo });
      }

      onCancel();
    } catch (error) {
      console.error("Failed to handle invitation:", error);
      const errorMessage =
        selectedTab === UsersSettingsTabs.inactive
          ? "Failed to send invite"
          : "Failed to send login instructions";

      customToast(errorMessage, "error", "bottom");
    }
  };

  const handleDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleDelete = async () => {
    if (deactivateUser.isPending) return;
    try {
      await deactivateUser.mutateAsync({ userId: user.id });
      customToast("User has been deactivated", "success", "bottom");
      setShowDeleteConfirmation(false);
      onCancel();
    } catch (error) {
      console.error("Failed to deactivate user:", error);
      customToast("Failed to deactivate user", "error", "bottom");
    }
  };

  const handleEditRole = () => {
    setCurrentView("editRole");
  };

  const handleEditPrivileges = () => {
    setCurrentView("editPrivileges");
  };

  const handleRoleDialogClose = () => {
    setCurrentView("details");
  };

  const handleCloseAll = () => {
    setCurrentView("details");
    onCancel();
  };

  const isButtonLoading =
    resendInvitationMutation.isPending ||
    onboardFoodicsAccounts.isPending ||
    deactivateUser.isPending;

  if (!show) {
    return null;
  }

  if (showDeleteConfirmation) {
    return (
      <Dialog
        show={true}
        message={"Are you sure you want to deactivate this user?"}
        confirmButton={{
          text: "Confirm",
          onClick: handleDelete,
          disabled: deactivateUser.isPending,
        }}
        onDismiss={handleCancelDelete}
        messageClassName="py-2.5 px-4"
        width="368px"
      />
    );
  }

  if (currentView === "editRole") {
    return (
      <EditRoleDialog
        show={true}
        onCancel={handleRoleDialogClose}
        user={user}
        onRoleChangeSuccess={handleCloseAll}
      />
    );
  }

  if (currentView === "editPrivileges") {
    return (
      <EditPrivilegesDialog
        show={true}
        onCancel={() => setCurrentView("details")}
        user={user}
        onPrivilegesChangeSuccess={handleCloseAll}
      />
    );
  }

  return (
    <Dialog
      show={true}
      className="w-[50%] min-w-[300px]"
      message={
        <>
          <div className="mb-2 text-xs text-dark">Name</div>
          <div className="w-full bg-pureWhite border border-lightGray text-feintGray rounded-lg p-2 mb-4">
            {user.name || "—"}
          </div>
          <div className="mb-2 text-xs text-dark">Status</div>
          <div className="w-full bg-pureWhite border border-lightGray text-feintGray rounded-lg p-2 mb-4 flex justify-between items-center">
            <span>{userStatus}</span>
            {(auth.user?.is_demo ?? false) ? null : selectedTab !== UsersSettingsTabs.active ? (
              <button
                className="text-primaryColor hover:text-primaryColor/80 font-medium"
                onClick={handleInvite}
                disabled={isButtonLoading}
              >
                {isButtonLoading ? "Loading..." : buttonText}
              </button>
            ) : (
              user.id !== auth.user?.id && (
                <button
                  className="text-primaryColor hover:text-primaryColor/80 font-medium"
                  onClick={handleDeleteConfirmation}
                  disabled={isButtonLoading}
                >
                  Deactivate
                </button>
              )
            )}
          </div>
          <div className="mb-2 text-xs text-dark">Email</div>
          <div className="w-full bg-pureWhite border border-lightGray text-feintGray rounded-lg p-2 mb-4">
            {user.email || "—"}
          </div>
          <div className="mb-2 text-xs text-dark">Branches</div>
          <div className="w-full bg-pureWhite border border-lightGray text-feintGray rounded-lg p-2 mb-4">
            {user.branchesCount === 1 ? "1 Branch" : `${user.branchesCount ?? 0} Branches`}
          </div>
          {selectedTab === UsersSettingsTabs.active && (
            <>
              <div className="mb-2 text-xs text-dark">Role</div>
              <div className="w-full bg-pureWhite border border-lightGray text-feintGray rounded-lg p-2 mb-4 flex justify-between items-center">
                <span>{user.is_root ? "Admin" : "Normal"}</span>
                {!user.is_root && !auth.user?.is_demo && (
                  <button
                    className="text-primaryColor hover:text-primaryColor/80 font-medium"
                    onClick={handleEditRole}
                  >
                    Edit
                  </button>
                )}
              </div>

              <div className="mb-2 text-xs text-dark">Privileges</div>
              <div className="w-full bg-pureWhite border border-lightGray text-feintGray rounded-lg p-2 mb-4 flex justify-between items-center">
                <span>{formatPrivileges(user.web_app_privileges, "No Privileges")}</span>
                {!auth.user?.is_demo && (
                  <button
                    className="text-primaryColor hover:text-primaryColor/80 font-medium"
                    onClick={handleEditPrivileges}
                  >
                    Edit
                  </button>
                )}
              </div>
            </>
          )}
        </>
      }
      showBorder={false}
      confirmButton={{ show: false }}
      onDismiss={handleCloseAll}
    />
  );
}
